import React, { useState, useEffect } from 'react';
import {
  Edit,
  SimpleForm,
  TextInput,
  SelectInput,
  BooleanInput,
  required,
  ResourceContextProvider,
  useDataProvider,
  useNotify,
  useRecordContext,
  fetchUtils,
} from 'react-admin';
import { tenantService } from '../../services/tenantService';
import { ColorPicker, ColorChip } from '../../components/shared';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField as MuiTextField,
  FormControlLabel,
  Checkbox,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
} from '@mui/icons-material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useParams } from 'react-router-dom';

interface BoardStage {
  id?: string;
  name: string;
  description?: string;
  color?: string;
  sortOrder: number;
  isDefault: boolean;
  isClosedWon: boolean;
  isClosedLost: boolean;
}

const StageDialog = ({
  open,
  onClose,
  stage,
  onSave,
  isEdit = false,
  stages,
}: {
  open: boolean;
  onClose: () => void;
  stage?: BoardStage;
  onSave: (stage: BoardStage) => void;
  isEdit?: boolean;
  stages: BoardStage[];
}) => {
  const [formData, setFormData] = useState<BoardStage>({
    name: '',
    description: '',
    color: 'blue',
    sortOrder: 0,
    isDefault: false,
    isClosedWon: false,
    isClosedLost: false,
    ...stage,
  });

  // Update form data when stage prop changes
  useEffect(() => {
    if (stage) {
      setFormData({
        name: stage.name || '',
        description: stage.description || '',
        color: stage.color || 'blue',
        sortOrder: stage.sortOrder || 0,
        isDefault: stage.isDefault || false,
        isClosedWon: stage.isClosedWon || false,
        isClosedLost: stage.isClosedLost || false,
        ...stage,
      });
    } else {
      // Reset to default values when no stage (adding new stage)
      // Set sortOrder to the next available number
      const maxSortOrder = Math.max(0, ...stages.map(s => s.sortOrder || 0));
      setFormData({
        name: '',
        description: '',
        color: 'blue',
        sortOrder: maxSortOrder + 1,
        isDefault: false,
        isClosedWon: false,
        isClosedLost: false,
      });
    }
  }, [stage, open, stages]);

  const handleSave = () => {
    if (!formData.name.trim()) {
      return;
    }
    onSave(formData);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Edit Stage' : 'Add New Stage'}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <MuiTextField
            label="Stage Name"
            value={formData.name}
            onChange={e => setFormData({ ...formData, name: e.target.value })}
            fullWidth
            required
          />
          <MuiTextField
            label="Description"
            value={formData.description}
            onChange={e => setFormData({ ...formData, description: e.target.value })}
            fullWidth
            multiline
            rows={2}
          />
          <ColorPicker
            value={formData.color}
            onChange={(color) => setFormData({ ...formData, color })}
            label="Color"
            fullWidth
          />
          <MuiTextField
            label="Sort Order"
            type="number"
            value={formData.sortOrder}
            onChange={e =>
              setFormData({ ...formData, sortOrder: parseInt(e.target.value, 10) || 0 })
            }
            fullWidth
          />
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.isDefault}
                  onChange={e => setFormData({ ...formData, isDefault: e.target.checked })}
                />
              }
              label="Default stage for new opportunities"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.isClosedWon}
                  onChange={e => setFormData({ ...formData, isClosedWon: e.target.checked })}
                />
              }
              label="Closed Won stage"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.isClosedLost}
                  onChange={e => setFormData({ ...formData, isClosedLost: e.target.checked })}
                />
              }
              label="Closed Lost stage"
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">
          {isEdit ? 'Update' : 'Add'} Stage
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const StageManagement = () => {
  const record = useRecordContext();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [stages, setStages] = useState<BoardStage[]>([]);
  const [stageDialog, setStageDialog] = useState({ open: false, stage: null, isEdit: false });

  useEffect(() => {
    if (record?.stages) {
      const sortedStages = [...record.stages].sort((a, b) => a.sortOrder - b.sortOrder);
      setStages(sortedStages);
    }
  }, [record]);

  const handleCreateStage = async (stageData: BoardStage) => {
    try {
      const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;
      const httpClient = fetchUtils.fetchJson;

      // Add authentication and tenant headers
      const accessToken = localStorage.getItem('accessToken');
      const options = {
        method: 'POST',
        body: JSON.stringify(stageData),
        headers: new Headers({
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
          'X-Tenant-Code': tenantService.getCurrentTenantCode(),
        }),
      };

      await httpClient(`${baseUrl}/crm/boards/${record.id}/stages`, options);
      notify('Stage created successfully', { type: 'success' });
      // Refresh board data
      window.location.reload();
    } catch (error) {
      notify('Failed to create stage', { type: 'error' });
      console.error('Create stage error:', error);
    }
  };

  const handleUpdateStage = async (stageData: BoardStage) => {
    try {
      const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;
      const httpClient = fetchUtils.fetchJson;

      // Add authentication and tenant headers
      const accessToken = localStorage.getItem('accessToken');
      const options = {
        method: 'PATCH',
        body: JSON.stringify(stageData),
        headers: new Headers({
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
          'X-Tenant-Code': tenantService.getCurrentTenantCode(),
        }),
      };

      await httpClient(`${baseUrl}/crm/boards/${record.id}/stages/${stageData.id}`, options);
      notify('Stage updated successfully', { type: 'success' });
      // Refresh board data
      window.location.reload();
    } catch (error) {
      notify('Failed to update stage', { type: 'error' });
      console.error('Update stage error:', error);
    }
  };

  const handleDeleteStage = async (stageId: string) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Are you sure you want to delete this stage?')) {
      return;
    }

    try {
      const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;
      const httpClient = fetchUtils.fetchJson;

      // Add authentication and tenant headers
      const accessToken = localStorage.getItem('accessToken');
      const options = {
        method: 'DELETE',
        headers: new Headers({
          'Authorization': `Bearer ${accessToken}`,
          'X-Tenant-Code': tenantService.getCurrentTenantCode(),
        }),
      };

      await httpClient(`${baseUrl}/crm/boards/${record.id}/stages/${stageId}`, options);
      notify('Stage deleted successfully', { type: 'success' });
      // Refresh board data
      window.location.reload();
    } catch (error) {
      notify('Failed to delete stage', { type: 'error' });
      console.error('Delete stage error:', error);
    }
  };


  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Board Stages</Typography>
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            onClick={() => setStageDialog({ open: true, stage: null, isEdit: false })}
          >
            Add Stage
          </Button>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Order</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Color</TableCell>
                <TableCell>Flags</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {stages.map(stage => (
                <TableRow key={stage.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DragIcon sx={{ color: 'text.secondary' }} />
                      {stage.sortOrder}
                    </Box>
                  </TableCell>
                  <TableCell>{stage.name}</TableCell>
                  <TableCell>{stage.description || '-'}</TableCell>
                  <TableCell>
                    <ColorChip color={stage.color || 'blue'} size="medium" />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {stage.isDefault && <Chip label="Default" size="small" color="primary" />}
                      {stage.isClosedWon && <Chip label="Won" size="small" color="success" />}
                      {stage.isClosedLost && <Chip label="Lost" size="small" color="error" />}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => setStageDialog({ open: true, stage, isEdit: true })}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteStage(stage.id!)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {stages.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
            No stages configured. Add your first stage to get started.
          </Box>
        )}

        <StageDialog
          open={stageDialog.open}
          onClose={() => setStageDialog({ open: false, stage: null, isEdit: false })}
          stage={stageDialog.stage}
          onSave={stageDialog.isEdit ? handleUpdateStage : handleCreateStage}
          isEdit={stageDialog.isEdit}
          stages={stages}
        />
      </CardContent>
    </Card>
  );
};

export const BoardEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box
          component="button"
          onClick={() => navigate('/boards')}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            border: '1px solid #ddd',
            borderRadius: 1,
            px: 2,
            py: 1,
            bgcolor: 'background.paper',
            cursor: 'pointer',
            '&:hover': {
              bgcolor: 'grey.100',
            },
            transition: 'all 0.2s ease',
          }}
        >
          <ArrowBackIcon fontSize="small" />
          <Typography variant="body2">Back to Boards</Typography>
        </Box>
      </Box>

      <ResourceContextProvider value="boards">
        <Edit id={id} redirect="list">
          <SimpleForm>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Board Information
                </Typography>
                <TextInput source="name" label="Name" validate={required()} fullWidth />
                <TextInput source="description" label="Description" multiline rows={2} fullWidth />
                <SelectInput
                  source="type"
                  label="Type"
                  choices={[
                    { id: 'Sales', name: 'Sales' },
                    { id: 'Projects', name: 'Projects' },
                    { id: 'Leads', name: 'Leads' },
                    { id: 'Custom', name: 'Custom' },
                  ]}
                  validate={required()}
                  fullWidth
                />
                <BooleanInput source="isActive" label="Active" />
              </CardContent>
            </Card>

            <StageManagement />

            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Board Settings
                </Typography>
                <BooleanInput source="allowMultipleAssignees" label="Allow Multiple Assignees" />
                <BooleanInput source="requireDueDates" label="Require Due Dates" />
                <BooleanInput source="enableNotifications" label="Enable Notifications" />
                <BooleanInput source="archiveCompletedItems" label="Archive Completed Items" />
              </CardContent>
            </Card>
          </SimpleForm>
        </Edit>
      </ResourceContextProvider>
    </Box>
  );
};
